/**
 * Sign in page component.
 *
 * Authentication page for user login. Handles email and password validation,
 * displays error messages for failed login attempts, and redirects users
 * to their intended destination after successful authentication.
 *
 * Route: `/signin`
 * Requires authentication: No
 *
 * @module SignIn
 */

import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { BookOpen, Eye, EyeOff } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { isValidEmail } from "@/lib/validation";
import { logger } from "@/lib/logger";
import { ROUTES } from "@/lib/routeConstants";
import { useTranslation } from 'react-i18next';
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { BackToLandingLink } from "@/components/layout/BackToLandingLink";
import { GoogleSignInButton } from "@/components/GoogleSignInButton";

// "Sign in with Google" is only shown when a Client ID is configured, so the
// page is unchanged when the feature is off.
const GOOGLE_ENABLED = !!import.meta.env.VITE_GOOGLE_CLIENT_ID;

// Support address assembled at runtime (split so the literal never appears in
// the static HTML or JS bundle that email-harvesting bots scrape).
const SUPPORT_EMAIL = ["deutschgenie.contact", "gmail.com"].join("@");

export default function SignIn() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [supportEmailRevealed, setSupportEmailRevealed] = useState(false);

  const handleContactSupport = () => {
    // Reveal the address (fallback for users without a mail client) and open
    // a pre-filled draft. The mailto string is built here, never in markup.
    setSupportEmailRevealed(true);
    window.location.href = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(
      t('auth.signIn.forgotPasswordSubject', { defaultValue: 'Password reset request' }),
    )}`;
  };
  const { login, googleLogin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleGoogleCredential = async (idToken: string) => {
    setIsLoading(true);
    try {
      const result = await googleLogin(idToken);
      if (result.success) {
        toast.success(t('auth.success.welcomeBack'), {
          description: t('auth.success.welcomeBackDesc'),
        });
        const from = (location.state as { from?: { pathname: string } })?.from?.pathname || ROUTES.DASHBOARD;
        navigate(from, { replace: true });
      } else {
        const isRateLimited =
          result.code === "RATE_LIMIT_EXCEEDED" || result.status === 429;
        toast.error(
          isRateLimited ? t('auth.errors.tooManyAttempts') : t('auth.errors.signInFailed'),
          { description: result.message },
        );
      }
    } catch (error) {
      logger.error("Google login error", error);
      toast.error(t('auth.errors.connectionError'), {
        description: t('auth.errors.connectionErrorDesc'),
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!isValidEmail(email)) {
      toast.error(t('auth.errors.invalidEmail'), {
        description: t('auth.errors.invalidEmailDesc'),
      });
      return;
    }

    if (!password) {
      toast.error(t('auth.errors.passwordRequired'), {
        description: t('auth.errors.passwordRequiredDesc'),
      });
      return;
    }

    setIsLoading(true);

    try {
      const result = await login(email, password);
      if (result.success) {
        toast.success(t('auth.success.welcomeBack'), {
          description: t('auth.success.welcomeBackDesc'),
        });
        // Redirect to the page the user was trying to access, or dashboard
        const from = (location.state as { from?: { pathname: string } })?.from?.pathname || ROUTES.DASHBOARD;
        navigate(from, { replace: true });
      } else {
        const isRateLimited =
          result.code === "RATE_LIMIT_EXCEEDED" || result.status === 429;
        let description =
          result.message ||
          (isRateLimited
            ? t('auth.errors.rateLimitDesc')
            : t('auth.errors.invalidEmailOrPassword'));

        if (isRateLimited && result.retryAfter) {
          const minutes = Math.ceil(result.retryAfter / 60);
          description = `${description} Please try again in approximately ${minutes} minute${minutes === 1 ? "" : "s"}.`;
        }

        toast.error(
          isRateLimited ? t('auth.errors.tooManyAttempts') : t('auth.errors.signInFailed'),
          {
            description,
          }
        );
      }
    } catch (error) {
      logger.error("Login error", error);
      toast.error(t('auth.errors.connectionError'), {
        description: t('auth.errors.connectionErrorDesc'),
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="relative flex min-h-[100dvh] flex-col items-center justify-start bg-background px-4 pb-[max(1.5rem,env(safe-area-inset-bottom))] pt-16 sm:py-8 lg:justify-center">
      <div className="absolute right-4 top-4 sm:right-6 sm:top-6">
        <LanguageSwitcher />
      </div>
      <div className="w-full max-w-md">
        <div className="mb-4">
          <BackToLandingLink />
        </div>
        {/* Logo */}
        <div className="text-center mb-6 sm:mb-8">
          <Link to={ROUTES.LANDING} className="inline-flex items-center gap-2">
            <div className="bg-primary p-2 sm:p-3 rounded-lg shadow-sm">
              <BookOpen className="h-6 w-6 sm:h-8 sm:w-8 text-primary-foreground" />
            </div>
            <div className="text-left leading-tight">
              <h1 className="font-bold text-xl sm:text-2xl text-foreground">
                DeutschGenie
              </h1>
              <p className="text-xs sm:text-sm text-muted-foreground">
                {t('common.slogan')}
              </p>
            </div>
          </Link>
        </div>

        <Card className="shadow-sm border-0 sm:border">
          <CardHeader className="text-center pb-4 py-4 sm:py-6">
            <CardTitle className="text-xl sm:text-2xl">{t('auth.signIn.title')}</CardTitle>
            <CardDescription className="text-sm">
              {t('auth.signIn.description')}
            </CardDescription>
          </CardHeader>
          <form onSubmit={handleSubmit}>
            <CardContent className="space-y-4 px-4 sm:px-6">
              <div className="space-y-2">
                <Label htmlFor="email">{t('auth.signIn.email')}</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder={t('auth.signIn.emailPlaceholder')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">{t('auth.signIn.password')}</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder={t('auth.signIn.passwordPlaceholder')}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-0 top-0 h-full px-3"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                {t('auth.signIn.forgotPassword', { defaultValue: 'Forgot your password?' })}{' '}
                {supportEmailRevealed ? (
                  <a
                    href={`mailto:${SUPPORT_EMAIL}`}
                    className="text-primary hover:underline font-medium"
                  >
                    {SUPPORT_EMAIL}
                  </a>
                ) : (
                  <button
                    type="button"
                    onClick={handleContactSupport}
                    className="text-primary hover:underline font-medium"
                  >
                    {t('auth.signIn.forgotPasswordContact', { defaultValue: 'Email support' })}
                  </button>
                )}
              </p>
            </CardContent>
            <CardFooter className="flex flex-col space-y-4 px-4 sm:px-6 pb-6 sm:pb-8">
              <Button
                type="submit"
                className="w-full"
                variant="default"
                disabled={isLoading}
              >
                {isLoading ? t('auth.signIn.submitting') : t('auth.signIn.submit')}
              </Button>
              {GOOGLE_ENABLED && (
                <>
                  <div className="relative w-full">
                    <div className="absolute inset-0 flex items-center">
                      <span className="w-full border-t" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-card px-2 text-muted-foreground">
                        {t('auth.or', { defaultValue: 'or' })}
                      </span>
                    </div>
                  </div>
                  <GoogleSignInButton onCredential={handleGoogleCredential} />
                  <p className="text-xs text-muted-foreground text-center">
                    {t('auth.googleConsentNotice', { defaultValue: 'By continuing with Google you agree to our data processing terms.' })}
                  </p>
                </>
              )}
              <div className="text-center text-sm">
                <span className="text-muted-foreground">
                  {t('auth.signIn.noAccount')}{" "}
                </span>
                <Link
                  to={ROUTES.SIGN_UP}
                  className="text-primary hover:underline font-medium"
                >
                  {t('auth.signIn.signUpLink')}
                </Link>
              </div>
            </CardFooter>
          </form>
        </Card>
      </div>
    </div>
  );
}
